import { AlertCircle, TriangleAlert } from 'lucide-react'
import { useState } from 'react'

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { dateWithYear, formatCashAmount, formatCashExact, shortDate } from '@/lib/cash'
import { messages } from '@/lib/messages'
import { cn } from '@/lib/utils'
import type { CashStripPoint } from '@/services/http/cash'

export function CashFirmSelect({
  firms,
  firm,
  onChange,
}: {
  firms: { key: string; label: string }[]
  firm: string | undefined
  onChange: (firm: string) => void
}) {
  if (firms.length === 0) return null
  const items = firms.map((f) => ({ value: f.key, label: f.label }))
  return (
    <Select value={firm ?? null} items={items} onValueChange={(value) => onChange(value as string)}>
      <SelectTrigger aria-label={messages.cash.weekly.firm} className="w-56">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {items.map((item) => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  )
}

const BAR_MAX = 28
/** Eksi ve eşik altı çubuklar renge ek olarak çizgili doldurulur (renk körlüğü, siyah-beyaz çıktı). */
const STRIPES = {
  backgroundImage:
    'repeating-linear-gradient(135deg, transparent 0 3px, color-mix(in oklab, var(--surface) 55%, transparent) 3px 5px)',
}

type StripStatus = 'negative' | 'belowMin' | null

function stripStatus(p: CashStripPoint): StripStatus {
  return p.closing < 0 ? 'negative' : p.below_min ? 'belowMin' : null
}

/**
 * Nakit şeridi (plan K2): önümüzdeki haftaların dönem sonu nakdi. Çubuk yüksekliği haftalar arası
 * en büyük mutlak değere göre, eksi çubuk aşağı ve kırmızı; eksi ve eşik altı haftalar renge ek
 * olarak simge ve çizgiyle işaretlenir. `minCash` verilirse eşik çizgisi çizilir. Üzerine gelinen
 * ya da seçilen haftanın tutarı başlıkta yazar; `onSelect` verilirse seçim üst bileşene de bildirilir.
 */
export function CashStrip({
  points,
  minCash,
  selectedEnd,
  onSelect,
}: {
  points: CashStripPoint[]
  minCash?: number
  selectedEnd?: string | null
  onSelect?: (point: CashStripPoint) => void
}) {
  const t = messages.cash.strip
  const [hover, setHover] = useState<string | null>(null)
  const [pinned, setPinned] = useState<string | null>(null)
  const threshold = minCash !== undefined && minCash > 0 ? minCash : null
  const max = Math.max(1, threshold ?? 0, ...points.map((p) => Math.abs(p.closing)))
  // `selectedEnd` verilmişse seçim üst bileşendedir (null = seçim yok); yoksa şerit kendi tutar.
  const activeEnd = hover ?? (selectedEnd !== undefined ? selectedEnd : pinned)
  const active = points.find((p) => p.end === activeEnd)
  const activeStatus = active ? stripStatus(active) : null
  const statusText = { negative: t.negative, belowMin: t.belowMin }
  return (
    <section aria-label={t.title} className="bg-surface flex flex-col gap-2 rounded-lg border p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 className="text-sm font-semibold">{t.title}</h2>
        <p className="text-text-secondary text-xs">{t.description}</p>
      </div>
      <p
        className="flex min-h-5 flex-wrap items-center gap-x-3 gap-y-1 text-xs"
        aria-live="polite"
        data-testid="cash-strip-caption"
      >
        {active ? (
          <>
            <span className="font-medium tabular-nums">
              {t.point(dateWithYear(active.end), formatCashAmount(active.closing))}
            </span>
            {activeStatus && (
              <span className={activeStatus === 'negative' ? 'text-danger' : 'text-warning'}>
                {statusText[activeStatus]}
              </span>
            )}
          </>
        ) : (
          <span className="text-text-secondary">{t.idle}</span>
        )}
        {threshold !== null && (
          <span className="text-text-secondary ml-auto flex items-center gap-1.5">
            <span aria-hidden className="border-warning inline-block w-4 border-t border-dashed" />
            {t.threshold(formatCashAmount(threshold))}
          </span>
        )}
      </p>
      <div className="overflow-x-auto">
        <div className="relative min-w-[26rem]">
          {threshold !== null && (
            <div
              aria-hidden
              data-testid="cash-strip-threshold"
              className="border-warning pointer-events-none absolute inset-x-0 border-t border-dashed"
              style={{ top: BAR_MAX - Math.round((threshold / max) * BAR_MAX) }}
            />
          )}
          <ol className="grid grid-cols-[repeat(13,minmax(0,1fr))] gap-1">
            {points.map((p) => {
              const height = Math.max(2, Math.round((Math.abs(p.closing) / max) * BAR_MAX))
              const negative = p.closing < 0
              const state = stripStatus(p)
              const label = [
                t.point(dateWithYear(p.end), formatCashExact(p.closing)),
                state && statusText[state],
              ]
                .filter(Boolean)
                .join(', ')
              const selected = p.end === activeEnd
              const Icon = negative ? AlertCircle : TriangleAlert
              return (
                <li key={p.end} data-end={p.end} data-closing={p.closing}>
                  <button
                    type="button"
                    title={onSelect ? `${label}. ${t.showWeek}` : label}
                    aria-label={label}
                    aria-pressed={selected}
                    className={cn(
                      'focus-visible:ring-ring flex w-full cursor-pointer flex-col items-center gap-1 rounded-sm outline-none focus-visible:ring-2',
                      selected && 'bg-surface-muted',
                    )}
                    onMouseEnter={() => setHover(p.end)}
                    onMouseLeave={() => setHover(null)}
                    onFocus={() => setHover(p.end)}
                    onBlur={() => setHover(null)}
                    onClick={() => {
                      setPinned(p.end)
                      onSelect?.(p)
                    }}
                  >
                    <div className="flex h-14 w-full flex-col justify-center">
                      <div className={cn('flex h-7 items-end', negative && 'invisible')}>
                        <div
                          className={cn(
                            'w-full rounded-sm',
                            p.below_min ? 'bg-warning' : 'bg-success',
                          )}
                          style={p.below_min ? { height, ...STRIPES } : { height }}
                        />
                      </div>
                      <div className={cn('flex h-7 items-start', !negative && 'invisible')}>
                        <div
                          className="bg-danger w-full rounded-sm"
                          style={{ height, ...STRIPES }}
                        />
                      </div>
                    </div>
                    <span className="text-text-secondary flex h-4 items-center gap-0.5 text-[10px] tabular-nums">
                      {state && (
                        <Icon
                          aria-hidden
                          className={cn('size-2.5', negative ? 'text-danger' : 'text-warning')}
                        />
                      )}
                      {shortDate(p.end)}
                    </span>
                  </button>
                </li>
              )
            })}
          </ol>
        </div>
      </div>
    </section>
  )
}
